// Counts visits to the Store's public pages with 3DLabs' own visitor counter (Umami, cookieless). The counter is
// loaded from the Store's own address. It sends the page address (only utm_ campaign tags are kept from what follows
// the "?"), the page title, a few shop actions (add to cart, checkout, contact taps, links-page taps, the order total
// with no order number or customer details) and how fast the page loaded.
// It is never loaded for: staff devices, or visitors who ask not to be tracked (Do Not Track or Global Privacy
// Control). Order pages never count as page views. The server only accepts it on the Store's own address and drops
// anything not on its own allow-list (server/store-visits.js).
const COUNTED = new Set(['home', 'catalog', 'product', 'links', 'privacy', 'returns', 'na-miata']);
const STAFF_KEY = '3dlabs.storeStaff';

// Our own checks open the Store with ?via=leader-check (or test, qa, check, audit, smoke): that tab never counts,
// even after it moves on to other pages. Automated browsers (navigator.webdriver) never count either.
const TEST_KEY = '3dlabs.testVisit';
const TEST_VIA = /^(leader-check|test|qa|check|audit|smoke)(-[a-z0-9-]{0,40})?$/i;
const testVisit = () => {
  try {
    if (navigator.webdriver === true) return true;
    if (TEST_VIA.test(new URLSearchParams(location.search).get('via') || '')) sessionStorage.setItem(TEST_KEY, '1');
    return sessionStorage.getItem(TEST_KEY) === '1';
  } catch { return false; }
};

const optedOut = () => {
  try {
    if (testVisit()) return true;
    if (navigator.globalPrivacyControl === true) return true;
    if (['1', 'yes'].includes(String(navigator.doNotTrack || window.doNotTrack || ''))) return true;
    return Boolean(localStorage.getItem(STAFF_KEY));
  } catch { return true; }
};

let loading = null;
function counter() {
  if (!loading) loading = new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = '/a/s.js';
    script.defer = true;
    // "store" is a placeholder; the server swaps in the real id. Page views are sent by us, never automatically.
    // performance: page speed (Web Vitals) for the page it was loaded on.
    Object.assign(script.dataset, { websiteId: 'store', autoTrack: 'false', doNotTrack: 'true', excludeSearch: 'true', excludeHash: 'true', performance: 'true' });
    script.onload = () => resolve(window.umami || null);
    script.onerror = () => resolve(null);
    document.head.appendChild(script);
  });
  return loading;
}

// Campaign tags from the address (utm_source etc.), nothing else.
const UTM = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
export function campaignQuery(search = location.search) {
  const params = new URLSearchParams(search), kept = new URLSearchParams();
  for (const key of UTM) {
    const value = params.get(key);
    if (value && value.length <= 100) kept.set(key, value);
  }
  const text = kept.toString();
  return text ? `?${text}` : '';
}
const referrerOrigin = () => { try { return document.referrer ? new URL(document.referrer).origin : ''; } catch { return ''; } };

let last = '';
export function countVisit(route, title) {
  if (!COUNTED.has(route?.page) || optedOut()) return;
  const path = location.pathname.replace(/\/+$/, '') || '/store';
  if (path === last) return;
  last = path;
  const url = path + campaignQuery();
  counter().then((umami) => {
    if (!umami?.track) return;
    try { umami.track((props) => ({ ...props, url, title: String(title || '').slice(0, 120), referrer: referrerOrigin() })); }
    catch { /* counting never gets in the way */ }
  });
}

// Shop actions, sent as named events. Only these names and these details ever leave the page; no names, emails,
// addresses, order numbers or free text. `at` overrides the page address (the order total is counted as /store/checkout).
const EVENTS = {
  add_to_cart: ['product'],
  // The cart total is "value": only a finished order (order_placed) carries "revenue", which Umami counts as a sale.
  checkout_start: ['value', 'currency', 'items'],
  contact: ['channel'],
  links_click: ['target'],
  order_placed: ['revenue', 'currency'],
};
const cleanData = (name, data = {}) => {
  const out = {};
  for (const key of EVENTS[name] || []) {
    const value = data[key];
    if (typeof value === 'number' && Number.isFinite(value) && value >= 0 && value < 1e6) out[key] = Math.round(value * 100) / 100;
    else if (typeof value === 'string' && /^[A-Za-z0-9 _./-]{1,60}$/.test(value)) out[key] = value;
  }
  return out;
};
export function countEvent(name, data, { at } = {}) {
  if (!EVENTS[name] || optedOut()) return;
  const url = at || (location.pathname.replace(/\/+$/, '') || '/store');
  counter().then((umami) => {
    if (!umami?.track) return;
    try { umami.track((props) => ({ ...props, url, referrer: referrerOrigin(), name, data: cleanData(name, data) })); }
    catch { /* counting never gets in the way */ }
  });
}

// The Store's own action log (store/api.js track) also feeds the counter, mapped to the safe event names above.
export function countStoreAction(eventType, metadata = {}, payload = {}) {
  const dollars = (cents) => (Number.isFinite(Number(cents)) ? Number(cents) / 100 : undefined);
  if (eventType === 'store_cart_add') countEvent('add_to_cart', { product: payload.product_slug });
  else if (eventType === 'site_checkout_click') countEvent('checkout_start', { value: dollars(payload.price_cents), currency: payload.currency || 'CAD', items: 1 });
  else if (eventType === 'store_cart_checkout_click') countEvent('checkout_start', { value: dollars(payload.total_cents), currency: payload.currency || 'CAD', items: Number(payload.item_count) || undefined });
  else if (eventType === 'store_contact_click') countEvent('contact', { channel: metadata.channel });
  else if (eventType === 'store_link_click') countEvent('links_click', { target: metadata.label });
}

// The order total, once per order on this device: amount and currency only.
export function countOrder(order) {
  if (!order?.order_number || !(order.total_cents > 0)) return;
  if (!(order.state === 'paid' || (order.pay_method === 'cash' && !['cancelled', 'failed', 'refunded'].includes(order.state)))) return;
  const key = `3dlabs.counted.${String(order.order_number).replace(/[^A-Za-z0-9-]/g, '').slice(0, 40)}`;
  try { if (localStorage.getItem(key)) return; localStorage.setItem(key, '1'); } catch { return; }
  countEvent('order_placed', { revenue: order.total_cents / 100, currency: order.currency || 'CAD' }, { at: '/store/checkout' });
}
